import type { MediaVariant } from "../../types";
import { isLegacyMobileLandscapeVariant } from "../utils/mediaVariants";

export type AdaptivePlaybackDecision = {
  variant?: MediaVariant;
  direction: "hold" | "up" | "down";
  reason: string;
};

export const adaptiveVariantHeight = (variant: MediaVariant) => {
  const text = [variant.resolution, variant.quality, variant.label].join(" ");
  const explicit = text.match(/(?:^|\D)(360|480|540|720|1080|1440|2160)p(?:\D|$)/i);
  if (explicit) return Number(explicit[1]);
  const dimensions = text.match(/(\d{3,4})\s*[xX×]\s*(\d{3,4})/);
  if (dimensions) return Math.min(Number(dimensions[1]), Number(dimensions[2]));
  return /原始|original/i.test(text) ? 1440 : 720;
};

export const adaptiveVariantBitrate = (variant: MediaVariant) => {
  const text = [variant.bitrate, variant.quality, variant.label].join(" ").toLowerCase();
  const stated = text.match(/(\d+(?:\.\d+)?)\s*(mbps|kbps|m|k)\b/i);
  if (stated) {
    const value = Number(stated[1]);
    return /k/i.test(stated[2]) ? value * 1_000 : value * 1_000_000;
  }
  const height = adaptiveVariantHeight(variant);
  const base =
    height <= 480
      ? 800_000
      : height <= 720
        ? 1_800_000
        : height <= 1080
          ? 3_500_000
          : 6_000_000;
  const compression = /高压缩|compact|small|省流/i.test(text) ? 0.68 : 1;
  const codec = /av1|av01/i.test(variant.codec) ? 0.78 : 1;
  return Math.round(base * compression * codec);
};

export const smartBufferTargetSeconds = ({
  bandwidthBps,
  bitrateBps,
  recentStalls = 0,
}: {
  bandwidthBps: number;
  bitrateBps: number;
  recentStalls?: number;
}) => {
  const bandwidth = Math.max(250_000, bandwidthBps || 0);
  const bitrate = Math.max(250_000, bitrateBps || 0);
  const loadRatio = bitrate / bandwidth;
  const networkReserve =
    loadRatio >= 1.15
      ? 34
      : loadRatio >= 0.95
        ? 28
        : 8 + loadRatio * 14;
  const stallReserve = Math.min(8, Math.max(0, recentStalls) * 3);
  return Math.max(8, Math.min(40, networkReserve + stallReserve));
};

export const estimateSmartBufferWaitSeconds = ({
  bufferedSeconds,
  targetSeconds,
  bandwidthBps,
  bitrateBps,
}: {
  bufferedSeconds: number;
  targetSeconds: number;
  bandwidthBps: number;
  bitrateBps: number;
}) => {
  const remaining = Math.max(0, targetSeconds - bufferedSeconds);
  if (remaining <= 0.15) return 0;
  const bandwidth = Math.max(250_000, bandwidthBps || 0);
  const bitrate = Math.max(250_000, bitrateBps || 0);
  return Math.min(
    599,
    Math.max(1, Math.ceil((remaining * bitrate * 1.08) / bandwidth)),
  );
};

const adaptiveVariantTier = (variant: MediaVariant) => {
  const text = [variant.bitrate, variant.quality, variant.label]
    .join(" ")
    .toLowerCase();
  const compact = /高压缩|compact|small|省流/i.test(text);
  return adaptiveVariantHeight(variant) * 10 + (compact ? 0 : 1);
};

export const orderedAdaptiveVariants = (variants: MediaVariant[]) => {
  const desktop = variants.filter(
    (variant) =>
      variant.kind === "desktop" || isLegacyMobileLandscapeVariant(variant),
  );
  const candidates = desktop.length
    ? desktop
    : variants.filter((variant) => variant.kind !== "audio");
  return [...candidates].sort((left, right) => {
    const bitrate =
      adaptiveVariantBitrate(left) - adaptiveVariantBitrate(right);
    if (bitrate) return bitrate;
    return adaptiveVariantHeight(left) - adaptiveVariantHeight(right);
  });
};

export const initialAdaptiveVariant = (
  variants: MediaVariant[],
  bandwidthBps: number,
  viewportHeight: number,
  saveData = false,
) => {
  const ordered = orderedAdaptiveVariants(variants);
  if (!ordered.length) return undefined;
  if (saveData) return ordered[0];
  const budget = bandwidthBps > 0 ? bandwidthBps * 0.62 : 3_000_000;
  const heightLimit = Math.max(720, viewportHeight * 1.25);
  return (
    [...ordered]
      .reverse()
      .find(
        (variant) =>
          adaptiveVariantBitrate(variant) <= budget &&
          adaptiveVariantHeight(variant) <= heightLimit,
      ) || ordered[0]
  );
};

export const decideAdaptiveVariant = ({
  variants,
  currentId,
  bandwidthBps,
  bufferAheadSeconds,
  recentStalls,
  stableForMs,
  sinceSwitchMs,
  viewportHeight,
  fatal = false,
  allowDowngrade = true,
}: {
  variants: MediaVariant[];
  currentId: string;
  bandwidthBps: number;
  bufferAheadSeconds: number;
  recentStalls: number;
  stableForMs: number;
  sinceSwitchMs: number;
  viewportHeight: number;
  fatal?: boolean;
  allowDowngrade?: boolean;
}): AdaptivePlaybackDecision => {
  const ordered = orderedAdaptiveVariants(variants);
  const foundIndex = ordered.findIndex((variant) => variant.id === currentId);
  const index = foundIndex < 0 ? 0 : foundIndex;
  const current = ordered[index];
  if (!current || ordered.length < 2) {
    return { variant: current, direction: "hold", reason: "可用版本不足" };
  }

  if (fatal) {
    const currentTier = adaptiveVariantTier(current);
    const currentCodec = current.codec.toLowerCase();
    const compatibleCodec = ordered.find(
      (variant) =>
        variant.id !== current.id &&
        adaptiveVariantTier(variant) === currentTier &&
        variant.codec.toLowerCase() !== currentCodec,
    );
    const lowerTier = [...ordered]
      .slice(0, index)
      .reverse()
      .find((variant) => adaptiveVariantTier(variant) < currentTier);
    return {
      variant: compatibleCodec || lowerTier || current,
      direction: "down",
      reason: "播放错误，自动切换兼容版本",
    };
  }

  if (allowDowngrade && bufferAheadSeconds < 3 && recentStalls >= 3 && index > 0 && sinceSwitchMs >= 45_000) {
    const lowerTier = [...ordered]
      .slice(0, index)
      .reverse()
      .find(
        (variant) =>
          adaptiveVariantTier(variant) < adaptiveVariantTier(current),
      );
    if (!lowerTier) {
      return {
        variant: current,
        direction: "hold",
        reason: "已是最低画质，准备调整播放节点",
      };
    }
    return {
      variant: lowerTier,
      direction: "down",
      reason: "一分钟内多次卡顿，自动降低一档画质",
    };
  }

  const currentBitrate = adaptiveVariantBitrate(current);
  if (
    allowDowngrade &&
    index > 0 &&
    recentStalls >= 1 &&
    bandwidthBps > 0 &&
    bandwidthBps < currentBitrate * 1.15 &&
    bufferAheadSeconds < 1.5 &&
    sinceSwitchMs >= 60_000
  ) {
    const lowerTier = [...ordered]
      .slice(0, index)
      .reverse()
      .find(
        (variant) =>
          adaptiveVariantTier(variant) < adaptiveVariantTier(current),
      );
    if (!lowerTier) {
      return {
        variant: current,
        direction: "hold",
        reason: "已是最低画质，保持当前版本",
      };
    }
    return {
      variant: lowerTier,
      direction: "down",
      reason: "带宽余量不足，自动降低画质",
    };
  }

  const next = ordered
    .slice(index + 1)
    .find(
      (variant) =>
        adaptiveVariantTier(variant) > adaptiveVariantTier(current),
    );
  if (
    next &&
    recentStalls === 0 &&
    stableForMs >= 120_000 &&
    sinceSwitchMs >= 120_000 &&
    bufferAheadSeconds >= 12 &&
    adaptiveVariantHeight(next) <= Math.max(720, viewportHeight * 1.25) &&
    bandwidthBps >= adaptiveVariantBitrate(next) * 2
  ) {
    return {
      variant: next,
      direction: "up",
      reason: "播放稳定，自动提升画质",
    };
  }
  return {
    variant: current,
    direction: "hold",
    reason: "持续监测播放状态",
  };
};
